import { useState, useMemo, useEffect, useCallback } from 'react';

export function usePagination(items, { pageSize = 25, mode = 'paginated' } = {}) {
  const [page, setPage] = useState(1);

  // Reset to page 1 when items change (new filter/month)
  useEffect(() => {
    setPage(1);
  }, [items.length]);

  const totalPages = Math.max(1, Math.ceil(items.length / pageSize));

  const paginatedItems = useMemo(() => {
    if (mode === 'loadMore') {
      return items.slice(0, page * pageSize);
    }
    const start = (page - 1) * pageSize;
    return items.slice(start, start + pageSize);
  }, [items, page, pageSize, mode]);

  const hasNext = mode === 'loadMore'
    ? page * pageSize < items.length
    : page < totalPages;
  const hasPrev = page > 1;

  const nextPage = useCallback(() => {
    setPage((p) => Math.min(p + 1, mode === 'loadMore' ? Math.ceil(items.length / pageSize) : totalPages));
  }, [totalPages, items.length, pageSize, mode]);

  const prevPage = useCallback(() => {
    setPage((p) => Math.max(p - 1, 1));
  }, []);

  const rangeStart = mode === 'loadMore' ? 1 : (page - 1) * pageSize + 1;
  const rangeEnd = mode === 'loadMore' ? paginatedItems.length : Math.min(page * pageSize, items.length);

  return {
    paginatedItems,
    page,
    totalPages,
    setPage,
    nextPage,
    prevPage,
    hasNext,
    hasPrev,
    rangeStart,
    rangeEnd,
    totalItems: items.length,
  };
}
